import { Component, HostBinding, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { UserDto, UserProfileDto } from 'src/app/api/identity/models';
import { UsersService } from 'src/app/api/identity/services';
import { UserGroupDto } from 'src/app/api/sms-app/models';
import { UserGroupsService } from 'src/app/api/sms-app/services';
import { CONSTANTS } from 'src/app/shared/models/constants';
import {
  ChildRouteInterface,
  IParams,
  RouteInterface,
} from 'src/app/shared/models/user.model';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { RedirectService } from 'src/app/shared/services/redirect-service';
import {
  BrandingService,
  BrandProfile,
} from 'src/app/shared/branding/branding.service';

const BASE_CLASSES = 'main-sidebar elevation-4';

@Component({
  selector: 'app-sub-sidebar',
  templateUrl: './sub-sidebar.component.html',
  styleUrls: ['./sub-sidebar.component.scss'],
})
export class SubSidebarComponent implements OnInit {
  @HostBinding('class') classes: string = BASE_CLASSES;

  public menu = MENU;
  public loggedInUserName = '';
  userGroupOptions: UserDto[] = [];
  userParams: IParams = {
    page: 0,
    size: 20,
    userId: undefined,
  };
  private isAdmin = false;
  public loggedInUser: UserProfileDto = {};

  readonly brand: BrandProfile;

  constructor(
    private userService: UsersService,
    private toastr: ToastrService,
    private redirectService: RedirectService,
    private userGroupService: UserGroupsService,
    private localStorageService: LocalStorageService,
    branding: BrandingService
  ) {
    this.brand = branding.brand;
  }

  ngOnInit() {
    this.fetchLoggedInUser();
  }

  fetchLoggedInUser() {
    this.userService.getLoggedInUserDetails().subscribe({
      next: user => {
        if (user?.data) {
          this.loggedInUser = user?.data[0];
          this.redirectService.setPermissions(
            this.loggedInUser.Privileges || []
          );
          this.loggedInUserName = this.loggedInUser?.name || '';
          this.userParams.userId = this.loggedInUser?.id || undefined;
          this.isAdmin =
            this.loggedInUser.designation === CONSTANTS.ADMIN_DESIGNATION;

          this.fetchUserGroups(this.userParams, this.isAdmin);
          this.localStorageService.setItem(
            CONSTANTS.STORE_IS_ROLE_ADMIN,
            JSON.stringify(this.isAdmin)
          );
          this.localStorageService.setItem(
            CONSTANTS.STORE_USER_ID,
            JSON.stringify(this.loggedInUser.id)
          );
          this.localStorageService.setItem(
            CONSTANTS.STORE_USER_NAME,
            this.loggedInUser.userName as string
          );
        }
      },
      error: error => {
        if (error.status !== 403) {
          this.toastr.error(error?.error?.message);
        }
      },
    });
  }

  fetchUserGroups(params: IParams, isAdmin: boolean) {
    this.userGroupService.getAllUserGroups(params).subscribe({
      next: response => {
        if (response?.data?.[0]) {
          this.userGroupOptions = response.data[0].data || [];
          if (this.userGroupOptions) {
            this.localStorageService.setItem(
              CONSTANTS.USER_GROUP,
              JSON.stringify(
                this.userGroupOptions.map((item: UserGroupDto) => item.id)
              )
            );
          }
          if (this.userGroupOptions.length > 0 || isAdmin) {
            this.localStorageService.setItem(
              CONSTANTS.AUTHORIZATION,
              CONSTANTS.VIEW_SMS_APPLICATION
            );
          }
        }
      },
      error: error => {
        if (error.status !== 403) {
          this.toastr.error(error?.error?.message);
        }
      },
    });
  }

  checkPermissions(item: RouteInterface): boolean {
    const authorizationMsg = localStorage.getItem(CONSTANTS.AUTHORIZATION);
    if (
      !authorizationMsg &&
      authorizationMsg !== CONSTANTS.VIEW_SMS_APPLICATION
    ) {
      return false;
    }

    if (!item) {
      return false;
    }

    if (item.permissions) {
      const hasPermission = item.permissions.every((permission: string) =>
        this.redirectService.hasPermission(permission)
      );

      if (!hasPermission) {
        return false;
      }
    }

    if (item.children && item.children.length > 0) {
      const visibleChildren = item.children.filter(
        (child: ChildRouteInterface) => {
          return this.checkPermissions(child);
        }
      );

      item.children = visibleChildren;

      return visibleChildren.length > 0;
    }

    return true;
  }
}

export const MENU = [
  {
    name: 'Send SMS',
    iconClasses: 'fas fa-comment-dots',
    path: ['/sms'],
    permissions: ['VIEW_MESSAGE_TAB'],
  },
  {
    name: 'Contacts',
    iconClasses: 'fa fa-address-card',
    path: ['/contacts'],
    permissions: ['VIEW_CONTACT_TAB'],
  },

  {
    name: 'Contact Groups',
    iconClasses: 'fa fa-users',
    path: ['/contact-groups'],
    permissions: ['VIEW_CONTACT_GROUP_TAB'],
  },
  {
    name: 'Sender',
    iconClasses: 'fa fa-paper-plane',
    path: ['/sender'],
    permissions: ['VIEW_SENDER_TAB'],
  },
  {
    name: 'User Groups',
    iconClasses: 'fa fa-user-friends',
    path: ['/user-groups'],
    permissions: ['VIEW_USER_GROUP_TAB'],
  },
  {
    name: 'Settings',
    iconClasses: 'fa fa-cog',
    path: ['/settings'],
    permissions: ['VIEW_SMS_SETTINGS_TAB'],
  },
];
